import { z } from 'zod'
import {
  periodoSchema,
  prioridadSchema,
  provenanceSchema,
} from './primitives.js'

/** Módulo Adquisición — Admin y Comercial. Sin campos de $$ para comercial. */

/** Etapa del funnel. Configurable: agregar una etapa es un registro. */
export const funnelStageSchema = z.object({
  id: z.string(),
  nombre: z.string(),
  orden: z.number().int(),
})
export type FunnelStage = z.infer<typeof funnelStageSchema>

export const funnelDatapointSchema = z.object({
  stageId: z.string(),
  /** null = agregado de todos los canales. */
  canalId: z.string().nullable(),
  periodo: periodoSchema,
  cantidad: z.number().int().nonnegative(),
  provenance: provenanceSchema,
})
export type FunnelDatapoint = z.infer<typeof funnelDatapointSchema>

export const channelStatusSchema = z.enum([
  'activo',
  'en_prueba',
  'pausado',
  'descartado',
])
export type ChannelStatus = z.infer<typeof channelStatusSchema>

export const CHANNEL_STATUS_LABELS: Record<ChannelStatus, string> = {
  activo: 'Activo',
  en_prueba: 'En prueba',
  pausado: 'Pausado',
  descartado: 'Descartado',
}

export const channelSchema = z.object({
  id: z.string(),
  nombre: z.string(),
  status: channelStatusSchema,
  /** Conversión de lead a ganada por este canal, en %. null = sin dato. */
  conversionPct: z.number().nullable(),
  escalabilidad: prioridadSchema,
  /** $$: null cuando el rol no puede ver plata. No es "sin dato". */
  costoMensual: z.number().nullable(),
  prioridad: prioridadSchema,
  nota: z.string().nullable(),
  provenance: provenanceSchema,
})
export type Channel = z.infer<typeof channelSchema>

export const seoAccionSchema = z.enum([
  'crear_contenido',
  'optimizar',
  'mantener',
  'descartar',
])
export type SeoAccion = z.infer<typeof seoAccionSchema>

export const SEO_ACCION_LABELS: Record<SeoAccion, string> = {
  crear_contenido: 'Crear contenido',
  optimizar: 'Optimizar',
  mantener: 'Mantener',
  descartar: 'Descartar',
}

/**
 * Query objetivo de SEO. La query textual es su clave en Search Console, y es
 * lo que arma el deep link al informe de rendimiento filtrado.
 */
export const seoQuerySchema = z.object({
  /** Clave natural: el texto exacto de la query en Search Console. */
  query: z.string(),
  periodo: periodoSchema,
  /** Posición promedio. null = sin dato. */
  posicion: z.number().nullable(),
  clics: z.number().nullable(),
  impresiones: z.number().nullable(),
  ctrPct: z.number().nullable(),
  /** Volumen de búsqueda estimado. Viene de research manual. */
  volumen: z.number().nullable(),
  /** Dificultad 0-100. */
  dificultad: z.number().nullable(),
  accion: seoAccionSchema,
  prioridad: prioridadSchema,
  provenance: provenanceSchema,
})
export type SeoQuery = z.infer<typeof seoQuerySchema>

/**
 * Oportunidad concreta dentro del funnel. Vive en Close: guarda el lead y la
 * oportunidad para poder abrir el registro exacto.
 */
export const funnelItemSchema = z.object({
  id: z.string(),
  nombre: z.string(),
  stageId: z.string(),
  canalId: z.string().nullable(),
  periodo: periodoSchema,
  /** $$: null cuando el rol no puede ver plata. */
  valorEstimado: z.number().nullable(),
  closeLeadId: z.string().nullable(),
  closeOpportunityId: z.string().nullable(),
  owner: z.object({ id: z.string(), nombre: z.string() }).nullable(),
  diasEnEtapa: z.number().int().nonnegative(),
})
export type FunnelItem = z.infer<typeof funnelItemSchema>

export const acquisitionPayloadSchema = z.object({
  periodo: periodoSchema,
  etapas: z.array(funnelStageSchema),
  datapoints: z.array(funnelDatapointSchema),
  canales: z.array(channelSchema),
  seoQueries: z.array(seoQuerySchema),
  oportunidades: z.array(funnelItemSchema),
})
export type AcquisitionPayload = z.infer<typeof acquisitionPayloadSchema>
